// src/pages/analytics/index.jsx
import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme, Skeleton, alpha } from "@mui/material";
import Header from "../../components/Header";
import InventoryOut from "../../components/InventoryOut";
import InventoryShare from "../../components/InventoryShare";
import SalesTrend from "../../components/SalesTrend";
import GrossProfitChart from "../../components/GrossProfitChart";
import { tokens } from "../../theme";

import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

// Services
import {
  getTotalBalanceSum,
  getTotalMonthlySales,
  getAverageOrderSummary,
} from "../../services/SaleService";
import { getInventorySummary } from "../../services/ProductService";

// utils
import {
  formatPercentChange,
  percentChangeColorKey,
} from "../../utils/numberUtils";

const Analytics = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [inventoryValue, setInventoryValue] = useState(null);
  const [inventoryValuePrev, setInventoryValuePrev] = useState(null);
  const [outOfStockCount, setOutOfStockCount] = useState(null);
  const [outOfStockCountPrev, setOutOfStockCountPrev] = useState(null);
  const [totalSalesRevenue, setTotalSalesRevenue] = useState(null);
  const [totalSalesRevenuePrev, setTotalSalesRevenuePrev] = useState(null);
  const [averageOrderValue, setAverageOrderValue] = useState(null);
  const [averageOrderValuePrev, setAverageOrderValuePrev] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchInventorySummary = async () => {
      try {
        const res = await getInventorySummary();
        if (!mounted) return;
        setInventoryValue(res?.data?.stockValue ?? 0);
        setInventoryValuePrev(res?.data?.stockValuePrevious ?? null);
        setOutOfStockCount(res?.data?.outOfStockCount ?? 0);
        setOutOfStockCountPrev(res?.data?.outOfStockCountPrevious ?? null);
      } catch (err) {
        console.error("Error fetching inventory summary:", err);
        if (mounted) {
          setInventoryValue(0);
          setInventoryValuePrev(null);
          setOutOfStockCount(0);
          setOutOfStockCountPrev(null);
        }
      }
    };

    const fetchTotalSales = async () => {
      try {
        const res = await getTotalBalanceSum();
        if (!mounted) return;
        setTotalSalesRevenue(res?.data ?? 0);

        const now = new Date();
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevMonth = prev.getMonth() + 1;
        const prevYear = prev.getFullYear();
        try {
          const prevRes = await getTotalMonthlySales(prevMonth, prevYear);
          if (!mounted) return;
          setTotalSalesRevenuePrev(prevRes?.data ?? null);
        } catch (errPrev) {
          console.error("Error fetching previous month total sales:", errPrev);
          if (mounted) setTotalSalesRevenuePrev(null);
        }
      } catch (err) {
        console.error("Error fetching total sales revenue:", err);
        if (mounted) {
          setTotalSalesRevenue(0);
          setTotalSalesRevenuePrev(null);
        }
      }
    };

    const fetchAverageOrderSummary = async () => {
      try {
        const res = await getAverageOrderSummary();
        if (!mounted) return;
        setAverageOrderValue(res?.data?.currentAvg ?? 0);
        setAverageOrderValuePrev(res?.data?.previousAvg ?? null);
      } catch (err) {
        console.error("Error fetching average order summary:", err);
        if (mounted) {
          setAverageOrderValue(0);
          setAverageOrderValuePrev(null);
        }
      }
    };

    fetchInventorySummary();
    fetchTotalSales();
    fetchAverageOrderSummary();

    return () => {
      mounted = false;
    };
  }, []);

  const formatCurrency = (val) => {
    if (val == null) return "₦0";
    const num = typeof val === "number" ? val : Number(val || 0);
    if (Number.isNaN(num)) return "₦0";
    return `₦${num.toLocaleString("en-NG", { maximumFractionDigits: 0 })}`;
  };

  // Helper to resolve semantic states to raw hex colors so MUI's alpha() works correctly
  const getBadgeColor = (status) => {
    if (status === "positive") return colors.greenAccent[500];
    if (status === "negative") return colors.redAccent[500];
    return colors.gray[400]; // neutral
  };

  const inventoryPercentStr = formatPercentChange(
    inventoryValue,
    inventoryValuePrev,
  );
  const inventoryPercentColor = getBadgeColor(
    percentChangeColorKey(inventoryValue, inventoryValuePrev),
  );

  const outOfStockPercentStr = formatPercentChange(
    outOfStockCount,
    outOfStockCountPrev,
  );
  const outOfStockPercentColor = getBadgeColor(
    percentChangeColorKey(outOfStockCount, outOfStockCountPrev),
  );

  const salesPercentStr = formatPercentChange(
    totalSalesRevenue,
    totalSalesRevenuePrev,
  );
  const salesPercentColor = getBadgeColor(
    percentChangeColorKey(totalSalesRevenue, totalSalesRevenuePrev),
  );

  const avgOrderPercentStr = formatPercentChange(
    averageOrderValue,
    averageOrderValuePrev,
  );
  const avgOrderPercentColor = getBadgeColor(
    percentChangeColorKey(averageOrderValue, averageOrderValuePrev),
  );

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "clip",
        overflowY: "visible",
        boxSizing: "border-box",
        p: { xs: 2, sm: 3, md: 4 },
        "@keyframes slideInUp": {
          "0%": { opacity: 0, transform: "translateY(20px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        sx={{
          animation: "slideInUp 0.5s ease-out forwards",
        }}
      >
        <Header
          title="INVENTORY & SALES ANALYTICS"
          subtitle="Analyze inventory levels and sales trends to optimize stock and boost revenue."
        />
      </Box>

      {}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="160px"
        gap="14px"
      >
        <Box
          gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.15s",
          }}
        >
          <Box
            width="100%"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            p="20px 24px"
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: "12px",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              cursor: "pointer",
              overflow: "hidden",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 12px 30px rgba(0,0,0,0.4)"
                    : "0 12px 30px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <Inventory2OutlinedIcon
                  sx={{ color: colors.blueAccent[500], fontSize: "32px" }}
                />
              </Box>
              {inventoryValuePrev !== null && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap="4px"
                  sx={{
                    backgroundColor: alpha(inventoryPercentColor, 0.15),
                    color: inventoryPercentColor,
                    padding: "4px 10px",
                    borderRadius: "20px",
                  }}
                >
                  {inventoryValue >= inventoryValuePrev ? (
                    <TrendingUpIcon sx={{ fontSize: "16px" }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: "16px" }} />
                  )}
                  <Typography
                    variant="caption"
                    fontWeight="700"
                    sx={{ fontSize: "0.75rem", lineHeight: 1 }}
                  >
                    {inventoryPercentStr}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box mt="16px">
              {inventoryValue == null ? (
                <Skeleton
                  variant="rounded"
                  width="80%"
                  height={32}
                  animation="wave"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                    mb: "4px",
                  }}
                />
              ) : (
                <Typography
                  variant="h3"
                  fontWeight="800"
                  sx={{
                    color: colors.gray[100],
                    mb: "4px",
                    letterSpacing: "-0.5px",
                    lineHeight: 1.2,
                  }}
                >
                  {formatCurrency(inventoryValue)}
                </Typography>
              )}
              <Typography
                variant="subtitle2"
                fontWeight="600"
                sx={{ color: colors.greenAccent[500] }}
              >
                Total Inventory Value
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.15s",
          }}
        >
          <Box
            width="100%"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            p="20px 24px"
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: "12px",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              cursor: "pointer",
              overflow: "hidden",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 12px 30px rgba(0,0,0,0.4)"
                    : "0 12px 30px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <ReportOutlinedIcon
                  sx={{ color: colors.redAccent[500], fontSize: "32px" }}
                />
              </Box>
              {outOfStockCountPrev !== null && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap="4px"
                  sx={{
                    backgroundColor: alpha(outOfStockPercentColor, 0.15),
                    color: outOfStockPercentColor,
                    padding: "4px 10px",
                    borderRadius: "20px",
                  }}
                >
                  {outOfStockCount >= outOfStockCountPrev ? (
                    <TrendingUpIcon sx={{ fontSize: "16px" }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: "16px" }} />
                  )}
                  <Typography
                    variant="caption"
                    fontWeight="700"
                    sx={{ fontSize: "0.75rem", lineHeight: 1 }}
                  >
                    {outOfStockPercentStr}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box mt="16px">
              {outOfStockCount == null ? (
                <Skeleton
                  variant="rounded"
                  width="80%"
                  height={32}
                  animation="wave"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                    mb: "4px",
                  }}
                />
              ) : (
                <Typography
                  variant="h3"
                  fontWeight="800"
                  sx={{
                    color: colors.gray[100],
                    mb: "4px",
                    letterSpacing: "-0.5px",
                    lineHeight: 1.2,
                  }}
                >
                  {outOfStockCount.toLocaleString()}
                </Typography>
              )}
              <Typography
                variant="subtitle2"
                fontWeight="600"
                sx={{ color: colors.greenAccent[500] }}
              >
                Products Out of Stock
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.15s",
          }}
        >
          <Box
            width="100%"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            p="20px 24px"
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: "12px",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              cursor: "pointer",
              overflow: "hidden",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 12px 30px rgba(0,0,0,0.4)"
                    : "0 12px 30px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <PointOfSaleOutlinedIcon
                  sx={{ color: colors.greenAccent[500], fontSize: "32px" }}
                />
              </Box>
              {totalSalesRevenuePrev !== null && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap="4px"
                  sx={{
                    backgroundColor: alpha(salesPercentColor, 0.15),
                    color: salesPercentColor,
                    padding: "4px 10px",
                    borderRadius: "20px",
                  }}
                >
                  {totalSalesRevenue >= totalSalesRevenuePrev ? (
                    <TrendingUpIcon sx={{ fontSize: "16px" }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: "16px" }} />
                  )}
                  <Typography
                    variant="caption"
                    fontWeight="700"
                    sx={{ fontSize: "0.75rem", lineHeight: 1 }}
                  >
                    {salesPercentStr}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box mt="16px">
              {totalSalesRevenue == null ? (
                <Skeleton
                  variant="rounded"
                  width="80%"
                  height={32}
                  animation="wave"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                    mb: "4px",
                  }}
                />
              ) : (
                <Typography
                  variant="h3"
                  fontWeight="800"
                  sx={{
                    color: colors.gray[100],
                    mb: "4px",
                    letterSpacing: "-0.5px",
                    lineHeight: 1.2,
                  }}
                >
                  {formatCurrency(totalSalesRevenue)}
                </Typography>
              )}
              <Typography
                variant="subtitle2"
                fontWeight="600"
                sx={{ color: colors.greenAccent[500] }}
              >
                Total Sales Revenue
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.15s",
          }}
        >
          <Box
            width="100%"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            p="20px 24px"
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: "12px",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              cursor: "pointer",
              overflow: "hidden",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 12px 30px rgba(0,0,0,0.4)"
                    : "0 12px 30px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <RequestQuoteOutlinedIcon
                  sx={{ color: colors.yellowAccent[500], fontSize: "32px" }}
                />
              </Box>
              {averageOrderValuePrev !== null && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap="4px"
                  sx={{
                    backgroundColor: alpha(avgOrderPercentColor, 0.15),
                    color: avgOrderPercentColor,
                    padding: "4px 10px",
                    borderRadius: "20px",
                  }}
                >
                  {averageOrderValue >= averageOrderValuePrev ? (
                    <TrendingUpIcon sx={{ fontSize: "16px" }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: "16px" }} />
                  )}
                  <Typography
                    variant="caption"
                    fontWeight="700"
                    sx={{ fontSize: "0.75rem", lineHeight: 1 }}
                  >
                    {avgOrderPercentStr}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box mt="16px">
              {averageOrderValue == null ? (
                <Skeleton
                  variant="rounded"
                  width="80%"
                  height={32}
                  animation="wave"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                    mb: "4px",
                  }}
                />
              ) : (
                <Typography
                  variant="h3"
                  fontWeight="800"
                  sx={{
                    color: colors.gray[100],
                    mb: "4px",
                    letterSpacing: "-0.5px",
                    lineHeight: 1.2,
                  }}
                >
                  {formatCurrency(averageOrderValue)}
                </Typography>
              )}
              <Typography
                variant="subtitle2"
                fontWeight="600"
                sx={{ color: colors.greenAccent[500] }}
              >
                Average Order Value
              </Typography>
            </Box>
          </Box>
        </Box>

        {}
        {/* Inventory Out Chart */}
        <Box
          gridColumn={{ xs: "span 12", lg: "span 6" }}
          gridRow="span 3"
          backgroundColor={colors.primary[400]}
          borderRadius="12px"
          border={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`}
          display="flex"
          flexDirection="column"
          boxShadow={
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)"
          }
          overflow="hidden"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.3s",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
            padding="20px 24px"
          >
            <Typography color={colors.gray[100]} variant="h4" fontWeight="700">
              Inventory Out (By Product)
            </Typography>
          </Box>
          <Box
            flex="1"
            sx={{
              minHeight: 0,
              minWidth: 0,
              overflow: "hidden",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InventoryOut isDashboard={true} />
          </Box>
        </Box>

        {/* Inventory Share Chart */}
        <Box
          gridColumn={{ xs: "span 12", lg: "span 6" }}
          gridRow="span 3"
          backgroundColor={colors.primary[400]}
          borderRadius="12px"
          border={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`}
          display="flex"
          flexDirection="column"
          boxShadow={
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)"
          }
          overflow="hidden"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.3s",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
            padding="20px 24px"
          >
            <Typography color={colors.gray[100]} variant="h4" fontWeight="700">
              Inventory Share
            </Typography>
          </Box>
          <Box
            flex="1"
            sx={{
              minHeight: 0,
              minWidth: 0,
              overflow: "hidden",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InventoryShare isDashboard={true} />
          </Box>
        </Box>

        {/* Sales Trend Chart */}
        <Box
          gridColumn="span 12"
          gridRow="span 3"
          backgroundColor={colors.primary[400]}
          borderRadius="12px"
          border={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`}
          display="flex"
          flexDirection="column"
          boxShadow={
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)"
          }
          overflow="hidden"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.45s",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
            padding="20px 24px"
          >
            <Typography color={colors.gray[100]} variant="h4" fontWeight="700">
              Sales Trend
            </Typography>
          </Box>
          <Box
            flex="1"
            sx={{
              minHeight: 0,
              minWidth: 0,
              overflow: "hidden",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SalesTrend isDashboard={true} />
          </Box>
        </Box>

        {/* Gross Profit Trend Chart */}
        <Box
          gridColumn="span 12"
          gridRow="span 3"
          backgroundColor={colors.primary[400]}
          borderRadius="12px"
          border={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`}
          display="flex"
          flexDirection="column"
          boxShadow={
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)"
          }
          overflow="hidden"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.45s",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
            padding="20px 24px"
          >
            <Typography color={colors.gray[100]} variant="h4" fontWeight="700">
              Gross Profit Trend
            </Typography>
          </Box>
          <Box
            flex="1"
            sx={{
              minHeight: 0,
              minWidth: 0,
              overflow: "hidden",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GrossProfitChart isDashboard={true} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Analytics;
